import React from 'react';
import { Row, Col, Card, Statistic, Badge, Tooltip } from 'antd';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { theme } from '../../theme';

export interface SummaryMetric {
  title: string;
  value: number | string;
  prefix?: React.ReactNode;
  suffix?: string;
  precision?: number;
  color?: string;
  loading?: boolean;
  badge?: { text: string; color: string };
  tooltip?: string;
  percentage?: number;
  trendData?: number[];
}

export interface DashboardSummaryProps {
  /** Título del resumen */
  title?: string;
  /** Métricas a mostrar (máximo 4 recomendado) */
  metrics: SummaryMetric[];
  /** Estilos adicionales */
  style?: React.CSSProperties;
}

const MiniTrendChart = ({ data, color }: { data: number[]; color: string }) => {
  const chartData = data.map((value, index) => ({ value }));

  return (
    <div style={{ width: '80px', height: '40px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Componente de resumen ejecutivo para dashboard
 * Muestra métricas clave en tarjetas destacadas
 * 
 * @example
 * <DashboardSummary
 *   title="Resumen Ejecutivo"
 *   metrics={[
 *     { title: "Stock Actual", value: 1200, suffix: "fardos", color: theme.colors.primary },
 *     { title: "Disponible", value: 1184, suffix: "fardos", color: theme.colors.success }
 *   ]}
 * />
 */
export const DashboardSummary: React.FC<DashboardSummaryProps> = ({
  title,
  metrics,
  style = {}
}) => {
  return (
    <div style={{ marginBottom: '32px', ...style }}>
      {title && (
        <h2 style={{
          fontSize: '20px',
          fontWeight: 600,
          color: theme.colors.primaryDark,
          marginBottom: '16px'
        }}>
          {title}
        </h2>
      )}
      <Row gutter={[16, 16]}>
        {metrics.map((metric, index) => {
          const cardContent = (
            <Card
              style={{
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${metric.color || theme.colors.primary}`,
                height: '100%'
              }}
              styles={{
                body: {
                  padding: '16px'
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Statistic
                    title={metric.title}
                    value={metric.value}
                    prefix={metric.prefix}
                    suffix={metric.suffix}
                    precision={metric.precision}
                    valueStyle={{
                      color: metric.color || theme.colors.primary,
                      fontSize: '24px',
                      fontWeight: 600
                    }}
                    loading={metric.loading}
                  />
                </div>
                {metric.trendData && metric.trendData.length > 0 && (
                  <MiniTrendChart data={metric.trendData} color={metric.color || theme.colors.primary} />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', gap: '8px' }}>
                {metric.badge && (
                  <Badge
                    color={metric.badge.color}
                    text={metric.badge.text}
                    style={{ fontSize: '12px' }}
                  />
                )}

                {metric.percentage !== undefined && (
                  <div style={{
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: theme.colors.textSecondary
                  }}>
                    {metric.percentage}%
                  </div>
                )}
              </div>
            </Card>
          );

          return (
            <Col xs={24} sm={12} md={6} key={index}>
              {metric.tooltip ? (
                <Tooltip title={metric.tooltip} placement="top">
                  {cardContent}
                </Tooltip>
              ) : (
                cardContent
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

